import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { UserSelector } from '@client/components/_shared/selectors/users/UserSelector';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Callout } from '@client/components/_ui/feedback/Callout';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { SkeletonBlock } from '@client/components/_ui/feedback/SkeletonBlock';
import { ExternalLink } from '@client/components/Common/Link';
import { ComplianceAuditTemplateTypeTag } from '@client/components/compliance-audit-templates/ComplianceAuditTemplateTypeTag';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUserId } from '@client/hooks/business/user/useUserId';
import { useFormDirty } from '@client/hooks/forms/useFormDirty';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { PERMISSIONS } from '@shared/permissions/permissions';
import { createFileRoute } from '@tanstack/react-router';
import { Form, Input, Select, Tag } from 'antd';
import { useId } from 'react';

export const Route = createFileRoute('/console/$orgId/tools/compliance-audit/overview/search/add')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'New compliance audit',
        },
        // Creating an audit is a write, so the read-only compliance audit reader
        // cannot open this page. The route guard evaluates every matched route, so
        // the parent's `compliance_audit:access` still applies alongside this.
        permissions: [PERMISSIONS.COMPLIANCE_AUDIT_MANAGE],
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ComplianceAuditAdd />
        </PageController>
    );
}

type ComplianceAudit = {
    insuredName: string;
    policyNumber: string;
    broker: string;
    ar?: string;
    ownerId: bigint;
    // antd Select cannot hold a bigint value — the option value travels as a
    // string and converts back with BigInt() at submit.
    complianceAuditTemplateId?: string;
};

const FormItem = Form.Item<ComplianceAudit>;

// The create form is add-only: an audit's header has no edit surface, and
// evidence enters an audit through the details page's file library, never here.
function ComplianceAuditAdd() {
    const formId = useId();
    const createComplianceAuditMutation = trpc.complianceAudit.createComplianceAudit.useMutation();
    const { trpcUtils } = useTrpc();
    const currentUserId = useUserId();
    const orgId = useOrgId();
    const navigate = useNavigate();

    // The template pin is immutable after creation (steps are cloned from the
    // pinned version), so the select belongs to creation only.
    const templateOptionsQuery = trpc.complianceAudit.listComplianceAuditTemplateOptions.useQuery(undefined, {
        refetchOnWindowFocus: false,
        meta: { errorSurface: 'inline' },
    });

    const [form] = Form.useForm<ComplianceAudit>();

    const formDirty = useFormDirty();
    const hasNoTemplates = templateOptionsQuery.isSuccess && templateOptionsQuery.data.rows.length === 0;

    return (
        <OverlayPage
            isDirty={formDirty.isDirty}
            title="New compliance audit"
            // Cancel only — a successful create navigates away before the close
            // animation runs, so this never fires on the happy path (see onSuccess).
            afterClose={() => {
                // Setup, loading and error states have no connected form to reset.
                if (templateOptionsQuery.isSuccess && !hasNoTemplates) form.resetFields();
                navigate({ moveInMatches: -1 });
            }}
            footer={({ requestClose }) => (
                <>
                    <Button type="secondary" onClick={requestClose}>
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        htmlType="submit"
                        form={formId}
                        // No editable form is rendered while the template options are
                        // loading, failed or empty, so block submit in those states.
                        disabled={
                            templateOptionsQuery.isPending || Boolean(templateOptionsQuery.error) || hasNoTemplates
                        }
                        loading={createComplianceAuditMutation.isPending}
                    >
                        Next
                    </Button>
                </>
            )}
        >
            {templateOptionsQuery.error ? (
                <InlineQueryError
                    error={templateOptionsQuery.error}
                    onRetry={() => void templateOptionsQuery.refetch()}
                    title="Couldn't load audit templates"
                />
            ) : templateOptionsQuery.isPending ? (
                <SkeletonBlock variant="form" rows={6} />
            ) : hasNoTemplates ? (
                <Callout role="status" variant="warning" title="No audit templates available">
                    <p className="m-0">
                        No audit templates are available to your organisation. Contact{' '}
                        <ExternalLink
                            href="mailto:support@getcurium.com"
                            target="_self"
                            className="text-warning-foreground underline decoration-current hover:text-warning-foreground hover:decoration-current active:text-warning-foreground"
                        >
                            Curium support
                        </ExternalLink>{' '}
                        to arrange a published template for your organisation.
                    </p>
                    <p className="m-0 mt-1">Quote organisation ID {String(orgId)} when contacting support.</p>
                </Callout>
            ) : (
                <Form
                    id={formId}
                    layout="vertical"
                    form={form}
                    {...formDirty.formProps}
                    initialValues={{ ownerId: currentUserId }}
                    // Edits arriving between Next and its response would otherwise be lost
                    // with the drawer, or turn a second Next into a second audit.
                    disabled={createComplianceAuditMutation.isPending}
                    onFinish={(values) => {
                        // CUR-668 — submit the version the option displayed, so the
                        // server rejects a template republished mid-form (CONFLICT)
                        // instead of silently pinning a version the user never saw.
                        // The required rule guards the id; the lookup narrows both.
                        const selectedTemplate = templateOptionsQuery.data.rows.find(
                            (row) => String(row.id) === values.complianceAuditTemplateId,
                        );
                        if (!values.complianceAuditTemplateId || !selectedTemplate) {
                            form.setFields([
                                {
                                    name: 'complianceAuditTemplateId',
                                    value: undefined,
                                    errors: ['This template is no longer available — select another template.'],
                                },
                            ]);
                            return;
                        }

                        message.loading('Saving...');

                        // Trim and collapse whitespace-only AR to unset so a blank
                        // input never persists as ''.
                        const trimmedAr = values.ar?.trim() || undefined;

                        createComplianceAuditMutation.mutate(
                            {
                                insuredName: values.insuredName,
                                policyNumber: values.policyNumber,
                                broker: values.broker,
                                ar: trimmedAr,
                                ownerId: values.ownerId,
                                complianceAuditTemplateId: BigInt(values.complianceAuditTemplateId),
                                templateVersion: selectedTemplate.version,
                            },
                            {
                                onSuccess(created) {
                                    // The audit exists now, so this draft is spent: the form is
                                    // disabled while the create is in flight, so there is no
                                    // later draft to protect and a second Next cannot create a
                                    // second audit.
                                    formDirty.markClean();
                                    message.success('Audit created.');
                                    // The navigation below unmounts this drawer with the
                                    // route change, so `afterClose` never runs — the list
                                    // cache has to be invalidated here or it stays stale.
                                    void trpcUtils.complianceAudit.invalidate();
                                    // `replace` so Back from the details page reaches the
                                    // list, not a re-opened empty create form. Only leaving the
                                    // drawer stops the navigation — it would otherwise drag the
                                    // user back from wherever they went.
                                    if (!formDirty.isActive()) return;
                                    navigate({
                                        to: '/console/$orgId/tools/compliance-audit/details/$complianceAuditId',
                                        params: { orgId, complianceAuditId: created.id },
                                        replace: true,
                                    });
                                },
                                onError(error) {
                                    errorMessage.show(error);
                                    // CUR-668 — a CONFLICT means the template was republished
                                    // mid-form. Refetch the options so the select shows the new
                                    // v{n} and a re-submit carries the fresh version, instead of
                                    // failing with the same conflict until the drawer is reopened.
                                    if (error.data?.code === 'CONFLICT') {
                                        void templateOptionsQuery.refetch();
                                    }
                                },
                            },
                        );
                    }}
                >
                    <FormItem
                        name="complianceAuditTemplateId"
                        label="Audit template"
                        rules={[{ required: true, message: 'Required' }]}
                    >
                        {/* CUR-754 — the first required control takes initial focus. The form only
                            mounts once the options have settled, so this lands after loading. */}
                        <Select
                            autoFocus
                            placeholder="Select the template this audit runs with"
                            options={templateOptionsQuery.data.rows.map((row) => ({
                                value: String(row.id),
                                label: (
                                    <span className="inline-flex items-center gap-2">
                                        {row.name}
                                        <ComplianceAuditTemplateTypeTag value={row.type} />
                                        {/* CUR-668 — which published version the audit pins,
                                            styled like the overview list's template column. */}
                                        {row.version != null && (
                                            <Tag variant="outlined" className="m-0">
                                                v{row.version}
                                            </Tag>
                                        )}
                                    </span>
                                ),
                            }))}
                        />
                    </FormItem>
                    <FormItem name="insuredName" label="Insured" rules={[{ required: true, message: 'Required' }]}>
                        <Input placeholder="e.g. Curium Pty Ltd" />
                    </FormItem>
                    <FormItem
                        name="policyNumber"
                        label="Policy number"
                        rules={[{ required: true, message: 'Required' }]}
                    >
                        <Input placeholder="e.g. POL-9923" />
                    </FormItem>
                    <FormItem name="broker" label="Broker" rules={[{ required: true, message: 'Required' }]}>
                        <Input placeholder="Broker name" />
                    </FormItem>
                    <FormItem name="ar" label="Authorised representative">
                        <Input placeholder="Optional" />
                    </FormItem>
                    <FormItem name="ownerId" label="Auditor" rules={[{ required: true, message: 'Required' }]}>
                        {/* This should be locked to the current user */}
                        <UserSelector placeholder="Select auditor" readonly />
                    </FormItem>
                </Form>
            )}
        </OverlayPage>
    );
}
